// Tools.js - Updated logic for API routing and Filtering Knowledge Tools

import React, { useState, useEffect, useContext, useMemo } from 'react';
import { UserContext } from '../UserContext';
import { Plus, Search } from 'react-feather';
import { API_BASE_URL } from '../config/api';
import PageContainer from '../components/PageContainer';
import ToolsSection from '../components/tools/ToolsSection';
import ToolForm from '../components/tools/ToolForm';
import { isKnowledgeTool } from '../utils/toolVisibility';
import '../styles/tools.css';
const extractErrorMessage = async (response, fallback) => {
  let payload;
  try {
    payload = await response.json();
  } catch {
    return fallback;
  }

  const detail = payload?.detail;
  if (typeof detail === 'string' && detail) return detail;

  if (Array.isArray(detail)) {
    const messages = detail
      .map(item => {
        if (typeof item === 'string') return item;
        const field = Array.isArray(item?.loc)
          ? item.loc.filter(part => part !== 'body').join('.')
          : '';
        const msg = item?.msg || '';
        if (!msg) return '';
        return field ? `${field}: ${msg}` : msg;
      })
      .filter(Boolean);
    if (messages.length) return messages.join('; ');
  }

  return fallback;
};

const Tools = () => {
  const { isAdmin } = useContext(UserContext);
  const [tools, setTools] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingTool, setEditingTool] = useState(null);
  const [saving, setSaving] = useState(false);
  const [deletingToolName, setDeletingToolName] = useState(null);
  const [query, setQuery] = useState('');

  // Search over name and description so a tool can be found by what it does as
  // well as what it is called. Mirrors the Agents page search.
  const visibleTools = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return tools;
    return tools.filter(tool => {
      const name = (tool.name || '').toLowerCase();
      const description = (tool.description || '').toLowerCase();
      return name.includes(q) || description.includes(q);
    });
  }, [tools, query]);

  const fetchTools = async () => {
    try {
      setLoading(true);
      setError(null);
      const token = localStorage.getItem('access_token');
      const response = await fetch(`${API_BASE_URL}/api/tools`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (!response.ok) throw new Error('Failed to fetch tools');
      const data = await response.json();

      setTools((data || []).filter(tool => !isKnowledgeTool(tool)));
    } catch (err) {
      setError('Failed to load tools. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTools();
  }, []);
  
  const showSuccessMessage = (message) => {
    setSuccessMessage(message);
    setTimeout(() => setSuccessMessage(null), 3000);
  };

  const handleAddTool = () => {
    setEditingTool(null);
    setShowAddForm(true);
  };

  const handleEditTool = (tool) => {
    setEditingTool(tool);
    setShowAddForm(true);
  };

  const handleDeleteTool = async (tool) => {
      const isCustom = tool.is_custom;
      const confirmMessage = isCustom
        ? 'Are you sure you want to delete this tool?'
        : 'Are you sure you want to permanently delete this global built-in tool? This action cannot be undone.';
        
      if (window.confirm(confirmMessage)) {
        try {
          setDeletingToolName(tool.name);

          setError(null);
          const token = localStorage.getItem('access_token');

          const response = await fetch(`${API_BASE_URL}/api/tools/${tool.id}`, {
            method: 'DELETE',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`
            },
          });

          if (!response.ok) {
            throw new Error(await extractErrorMessage(response, 'Failed to delete tool'));
          }

          setTools(prev => prev.filter(t => t.id !== tool.id));
          showSuccessMessage('Tool deleted successfully');

        } catch (err) {
          setError(err.message || 'Failed to delete tool.');
        }
        finally
        {
          setDeletingToolName(null);
        }
      }
    };

  const handleFormSubmit = async (toolData) => {
    if (saving) return;

    try {
      setSaving(true);
      setError(null);
      const token = localStorage.getItem('access_token');
      const isUpdate = !!editingTool;
      const isCustom = toolData.is_custom;

      let endpoint = '';
      let method = '';
      let body = '';

      if (isCustom) {
        // --- Handle User's CUSTOM tools ---
        // Convert JSON Schema params to List[ToolParameterDefinition]
        const props = toolData.parameters?.properties || {};
        const requiredParams = toolData.parameters?.required || [];
        const parameterList = Object.entries(props).map(([name, prop]) => {
          const definition = {
            name,
            type: prop.type || 'string',
            description: prop.description || `Parameter '${name}'`,
            required: requiredParams.includes(name),
            default: prop.default !== undefined ? prop.default : null,
            enum: prop.enum || null,
          };

          // Object params reach the agent as a JSON string. The example travels
          // with the definition so the agent is told which format to send.
          if (prop.type === 'object' && prop.example) {
            definition.example = prop.example;
          }

          return definition;
        });

        // Build ToolConfiguration set_keys (no None values allowed)
        const secretToken = toolData.configuration?.secret_token || '';
        const setKeys = {
          url: toolData.configuration?.url || '',
          verb: toolData.configuration?.verb || 'GET',
          headers: toolData.configuration?.headers || {},
        };
        if (secretToken) setKeys.secret_token = secretToken;

        const toolConfiguration = {
          provisioner_keys: [],
          set_keys: setKeys,
          secrets: secretToken ? ['secret_token'] : [],
        };

        if (isUpdate) {
          endpoint = `${API_BASE_URL}/api/tools/${editingTool.id}`;
          method = 'PUT';
          body = JSON.stringify({
            name: toolData.name,
            description: toolData.description,
            parameters: parameterList,
            configuration: toolConfiguration,
          });
        } else {
          endpoint = `${API_BASE_URL}/api/tools`;
          method = 'POST';
          body = JSON.stringify({
            tool_name: toolData.name,
            description: toolData.description,
            provision_request: { resource_type: 'none', parameters: {} },
            parameters: parameterList,
            configuration: toolConfiguration,
          });
        }
      } else if (isAdmin) {
        // --- Handle Admin's BUILT-IN tools ---
        endpoint = isUpdate
          ? `${API_BASE_URL}/api/admin/tools/${editingTool.id}`
          : `${API_BASE_URL}/api/admin/tools`;
        method = isUpdate ? 'PUT' : 'POST';
        // Admin endpoint expects the tool data directly
        body = JSON.stringify(toolData);
      } else {
        throw new Error("You do not have permission to create built-in tools.");
      }

      const response = await fetch(endpoint, {
        method: method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: body,
      });
      
      if (!response.ok) {
        throw new Error(await extractErrorMessage(response, 'Failed to save tool'));
      }
      
      showSuccessMessage(isUpdate ? 'Tool updated successfully' : 'Tool added successfully');
      setShowAddForm(false);
      setEditingTool(null);
      
      // Refetch tools (this will trigger the filter in fetchTools)
      await fetchTools();
      
    } catch (err) {
      setError(err.message || 'Failed to save tool.');
    }
    finally {
      setSaving(false);
     }
  };

  const handleCancelForm = () => {
    setShowAddForm(false);
    setEditingTool(null);
    setError(null);
  };

  return (
    <PageContainer
      loading={loading}
      loadingMessage="Loading tools..."
      title="Tools"
      subtitle="Manage your agent tools"
    >
      {!showAddForm && successMessage && (
        <div className="save-message success">{successMessage}</div>
      )}
      {!showAddForm && error && (
        <div className="save-message error">{error}</div>
      )}
      <div className="tools-section">
      {showAddForm ? (
        <ToolForm 
          initialData={editingTool}
          onSubmit={handleFormSubmit}
          onCancel={handleCancelForm}
          errorMessage={error}
          isAdmin={isAdmin}
          submitting={saving} 
        />
      ) : (
        <>
          <div className="tools-surface">
            <div className="tools-toolbar">
              <h2 className="tools-toolbar-title">
                Your Tools
                {tools.length > 0 && (
                  <span className="tools-count">{tools.length}</span>
                )}
              </h2>

              <div className="tools-toolbar-actions">
                {tools.length > 0 && (
                  <div className="tools-search">
                    <Search size={15} aria-hidden="true" />
                    <input
                      type="search"
                      value={query}
                      onChange={(e) => setQuery(e.target.value)}
                      placeholder="Search tools"
                      aria-label="Search tools"
                    />
                  </div>
                )}
                <button
                  type="button"
                  onClick={handleAddTool}
                  className="tools-btn-primary"
                >
                  <Plus size={18} aria-hidden="true" />
                  New Tool
                </button>
              </div>
            </div>

            <ToolsSection
              tools={visibleTools}
              onEdit={handleEditTool}
              onDelete={handleDeleteTool}
              isAdmin={isAdmin}
              deletingToolName={deletingToolName}
              isFiltered={query.trim().length > 0}
              onClearFilter={() => setQuery('')}
            />
          </div>
        </>
      )}
      </div>
    </PageContainer>
  );
};

export default Tools;
